import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import useSeo from "@/hooks/useSeo";
import { pickSampleSlug } from "@/lib/sample";
import { DonkeyMascot } from "@/components/DonkeyMascot";
import { ArrowRight, Check, Users, Search } from "lucide-react";
import { AmbientBackground } from "@/components/home/AmbientBackground";
import { HeroIntelligenceEngine } from "@/components/home/HeroIntelligenceEngine";
import { RotatingObservations } from "@/components/home/RotatingObservations";
import { KineticHeadline } from "@/components/home/KineticHeadline";
import { BrandSignature } from "@/components/home/BrandSignature";
import { HeroDonkey, DonkeyStageMobile } from "@/components/home/HeroDonkey";
import { ProcessJourney } from "@/components/home/ProcessJourney";
import { EvidenceFlow } from "@/components/home/EvidenceFlow";
import { OpportunityTournament } from "@/components/home/OpportunityTournament";
import { VerdictCalculator } from "@/components/home/VerdictCalculator";

const JOURNEY = [
  { t: "Tell us what you know", live: true },
  { t: "Discover three businesses built around you", live: true },
  { t: "Choose the strongest", live: true },
  { t: "Test it before spending serious money", live: true },
  { t: "Build and launch it with our help", live: false },
];

const BENEFITS = [
  { icon: Users, t: "Built around you", b: "Not another recycled list of side hustles. Your opportunities reflect what you know, what you can afford and what you could realistically deliver." },
  { icon: Search, t: "Researched honestly", b: "The Donkey investigates demand, challenges assumptions and shows the evidence, weaknesses and uncertainty." },
  { icon: ArrowRight, t: "One clear next step", b: "No business or technical knowledge required. The Donkey guides you without overwhelming you." },
];

const PROVE_INCLUDES = [
  "A guided interview built around you",
  "Three personalised business opportunities",
  "Live market research",
  "Honest scoring and a clear recommendation",
  "Choose, replace or reject the ideas",
  "One included rethink when none feels right",
  "A practical way to test the one you pick",
  "One clear next action throughout",
];

const revealProps = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.3 },
  transition: { duration: 0.55, ease: "easeOut" },
};

export default function Home() {
  const navigate = useNavigate();
  useSeo({
    title: "The Honest Donkey — Find, build and grow a business without the bullshit",
    description: "The Honest Donkey analyses your skills, experience and circumstances to identify a business you could credibly run—then helps you validate, brand, build, launch and grow it. Evidence, not hype.",
    path: "/",
  });
  return (
    <div className="grain">
      {/* HERO */}
      <section className="relative overflow-hidden bg-donkey-black min-h-[94vh] flex items-center border-b border-donkey-line">
        <AmbientBackground />
        <HeroDonkey />
        <div className="relative z-10 mx-auto max-w-7xl w-full px-5 sm:px-8 pt-16 pb-28 lg:py-20 grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          {/* ZONE 1 — MESSAGE */}
          <div className="lg:col-span-5 min-w-0">
            <motion.p
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="text-xs uppercase tracking-[0.2em] text-donkey-orange mb-6"
            >
              FIND IT. PROVE IT. BUILD IT.
            </motion.p>

            <KineticHeadline />

            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 1.0 }}
              className="mt-6 text-base sm:text-lg text-donkey-grey max-w-xl leading-relaxed"
            >
              The Honest Donkey finds businesses that fit your skills, experience, circumstances and budget—then helps you test the strongest before you risk serious time or money.
            </motion.p>

            <BrandSignature />

            <RotatingObservations className="mt-6 max-w-md" />

            <div className="mt-8 flex flex-wrap gap-4">
              <Button
                size="lg"
                className="group relative bg-donkey-orange text-donkey-black hover:bg-donkey-deeporange font-bold rounded-full px-8 h-12 text-base transition-all shadow-[0_4px_20px_rgba(249,115,22,0.35)] hover:shadow-[0_8px_36px_rgba(249,115,22,0.55)] hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-donkey-orange focus-visible:ring-offset-2 focus-visible:ring-offset-donkey-bg"
                onClick={() => navigate("/pricing")}
                data-testid="hero-talk-btn"
              >
                <span className="sheen-layer" aria-hidden />
                <span className="relative z-10 inline-flex items-center">
                  Find my business
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-1.5" />
                </span>
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate("/demo")}
                data-testid="hero-demo-btn"
                className="group border-donkey-orange/50 bg-transparent text-donkey-warm hover:bg-donkey-orange/10 hover:border-donkey-orange hover:-translate-y-0.5 font-semibold rounded-full px-7 h-12 text-base transition-all focus-visible:ring-2 focus-visible:ring-donkey-orange focus-visible:ring-offset-2 focus-visible:ring-offset-donkey-bg"
              >
                See the Donkey in action
                <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-1.5" />
              </Button>
            </div>
            <p className="mt-8 text-sm text-donkey-grey border-l-2 border-donkey-orange/60 pl-4 max-w-md">
              We take the complexity out of building a business and guide you through it.
            </p>
          </div>

          {/* ZONE 2 — LIVE INTELLIGENCE ENGINE */}
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.7, delay: 1.6, ease: "easeOut" }}
            className="lg:col-span-4 lg:col-start-6 min-w-0 lg:self-start lg:mt-[130px]"
          >
            <DonkeyStageMobile />
            <HeroIntelligenceEngine />
          </motion.div>
          {/* ZONE 3 — DONKEY (rendered as an animated stage behind, see HeroDonkey) */}
        </div>
      </section>

      {/* PROPOSITION STRIP */}
      <section className="relative border-t border-donkey-line bg-donkey-black">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-6 sm:py-7 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6">
          <p className="font-heading font-bold text-donkey-warm text-lg sm:text-xl shrink-0">
            Find it. Prove it. <span className="text-donkey-orange">Build it.</span>
          </p>
          <p className="text-sm sm:text-base text-donkey-grey leading-relaxed">
            The Honest Donkey turns what you already know into a business opportunity you can realistically test and build—with one clear next step at a time.
          </p>
        </div>
      </section>

      {/* CONNECTED INTELLIGENCE SEQUENCE (was three static cards) */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
        <motion.div
          initial="hide" whileInView="show" viewport={{ once: true, amount: 0.3 }}
          className="relative grid md:grid-cols-3 gap-6"
        >
          {/* connector that draws across the three stages */}
          <motion.div
            variants={{ hide: { scaleX: 0 }, show: { scaleX: 1 } }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className="hidden md:block absolute top-[2.6rem] left-[16%] right-[16%] h-px origin-left bg-gradient-to-r from-donkey-orange via-donkey-amber to-donkey-warmorange"
            aria-hidden
          />
          {BENEFITS.map((b, i) => (
            <motion.div
              key={i}
              variants={{ hide: { opacity: 0, y: 28 }, show: { opacity: 1, y: 0 } }}
              transition={{ duration: 0.55, delay: i * 0.28, ease: [0.22, 1, 0.36, 1] }}
              className="relative card-donkey rounded-2xl p-7 hover:border-donkey-orange/50 transition-colors duration-300"
              data-testid={`benefit-${i}`}
            >
              <div className="relative z-10 w-12 h-12 rounded-full bg-donkey-black border border-donkey-orange/40 flex items-center justify-center mb-5 shadow-[0_0_20px_rgba(249,115,22,0.25)]">
                <b.icon className="w-5 h-5 text-donkey-orange" />
              </div>
              <h3 className="font-heading font-semibold text-xl text-donkey-warm mb-2">{b.t}</h3>
              <p className="text-sm text-donkey-warm/70 leading-relaxed">{b.b}</p>
              {/* distinct mini-visual per stage */}
              <div className="mt-5 pt-5 border-t border-white/10 min-h-[3.2rem] flex flex-wrap items-center gap-1.5">
                {i === 0 && ["Skills", "£ Budget", "Time", "Reach"].map((c, j) => (
                  <motion.span key={c} variants={{ hide: { opacity: 0, scale: 0.8 }, show: { opacity: 1, scale: 1 } }} transition={{ delay: 0.5 + j * 0.12 }}
                    className="text-[11px] rounded-full border border-donkey-orange/30 bg-donkey-orange/10 text-donkey-warmorange px-2.5 py-1">{c} ✓</motion.span>
                ))}
                {i === 1 && ["Demand", "Competition", "Reachability"].map((c, j) => (
                  <div key={c} className="w-full flex items-center gap-2">
                    <span className="text-[11px] text-donkey-grey w-24 shrink-0">{c}</span>
                    <div className="flex-1 h-1.5 rounded-full bg-white/8 overflow-hidden">
                      <motion.div variants={{ hide: { width: 0 }, show: { width: `${60 + j * 12}%` } }} transition={{ delay: 0.55 + j * 0.15, duration: 0.7 }} className="h-full bg-gradient-to-r from-donkey-orange to-donkey-amber" />
                    </div>
                  </div>
                ))}
                {i === 2 && (
                  <motion.span variants={{ hide: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } }} transition={{ delay: 0.7 }}
                    className="inline-flex items-center gap-2 text-sm rounded-full border border-donkey-green/40 bg-donkey-green/10 text-donkey-green px-3 py-1.5">
                    <Check className="w-4 h-4" /> One recommended next step
                  </motion.span>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* SCROLL-ACTIVATED PROCESS JOURNEY */}
      <ProcessJourney />

      {/* EVIDENCE / RESEARCH FLOW */}
      <EvidenceFlow />

      {/* OPPORTUNITY TOURNAMENT */}
      <OpportunityTournament />

      {/* VERDICT CALCULATOR */}
      <VerdictCalculator />

      {/* THE SIMPLE JOURNEY */}
      <section className="relative overflow-hidden border-t border-donkey-line bg-donkey-deep">
        <div className="absolute right-0 top-0 w-[36rem] h-[36rem] rounded-full bg-donkey-orange/8 blur-[120px] pointer-events-none" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-20">
          <div className="w-16 h-1 rounded-full bg-donkey-orange mb-6" />
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-donkey-warm">The simple journey</h2>
          <div className="mt-12 grid gap-3 max-w-3xl" data-testid="journey-steps">
            {JOURNEY.map((s, i) => (
              <motion.div
                key={i}
                {...revealProps}
                transition={{ ...revealProps.transition, delay: i * 0.08 }}
                className="flex items-center gap-4 card-donkey rounded-xl p-5"
              >
                <span className="w-9 h-9 rounded-full bg-donkey-orange/15 border border-donkey-orange/30 flex items-center justify-center font-mono text-donkey-orange text-sm shrink-0">0{i + 1}</span>
                <span className="flex-1 text-donkey-warm font-medium">{s.t}</span>
                {s.live ? (
                  <span className="text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full border border-donkey-green/40 text-donkey-green bg-donkey-green/10 shrink-0">Available now</span>
                ) : (
                  <span className="text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full border border-donkey-amber/40 text-donkey-amber bg-donkey-amber/10 shrink-0">Later-plan preview</span>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DEMO PROOF */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
        <div className="card-donkey rounded-2xl p-6 sm:p-10 grid lg:grid-cols-3 gap-8 items-center">
          <div className="lg:col-span-2 min-w-0">
            <div className="w-16 h-1 rounded-full bg-donkey-orange mb-6" />
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-donkey-warm">See exactly how the Donkey works</h2>
            <p className="text-donkey-grey mt-4 leading-relaxed max-w-2xl">
              Follow a fictional customer from their experience to a researched business opportunity and a clear plan for testing it. No signup or payment required.
            </p>
            <Button
              size="lg"
              onClick={() => navigate("/demo")}
              data-testid="proof-demo-btn"
              className="mt-7 w-full sm:w-auto whitespace-normal sm:whitespace-nowrap min-h-12 sm:h-12 py-3 sm:py-0 bg-donkey-orange text-donkey-black hover:bg-donkey-deeporange font-bold rounded-full px-8 text-base transition-all shadow-[0_4px_20px_rgba(249,115,22,0.3)] hover:shadow-[0_6px_30px_rgba(249,115,22,0.45)] hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-donkey-orange focus-visible:ring-offset-2 focus-visible:ring-offset-donkey-bg"
            >
              Watch the 2-minute demo <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <p className="text-xs text-donkey-grey/80 mt-4">Illustrative example — not a real customer. Live features and later-plan previews are clearly labelled inside.</p>
          </div>
          <div className="flex justify-center lg:justify-end min-w-0">
            <DonkeyMascot size={150} className="opacity-95" />
          </div>
        </div>
      </section>

      {/* WHAT PROVE DELIVERS */}
      <section className="border-t border-white/10 bg-donkey-deep">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
          <div className="w-16 h-1 rounded-full bg-donkey-orange mb-6" />
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-donkey-warm">Stop wondering what business to start</h2>
          <p className="text-donkey-grey mt-4 max-w-2xl leading-relaxed">
            Prove is where it begins. In plain language, here's what you get before you ever think about spending money:
          </p>
          <div className="mt-10 grid sm:grid-cols-2 gap-4" data-testid="prove-includes">
            {PROVE_INCLUDES.map((t, i) => (
              <motion.div
                key={i}
                {...revealProps}
                transition={{ ...revealProps.transition, delay: (i % 2) * 0.08 }}
                className="flex items-start gap-3 card-donkey rounded-xl p-5"
              >
                <Check className="w-5 h-5 text-donkey-green shrink-0 mt-0.5" />
                <span className="text-sm text-donkey-warm/90">{t}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ BRIDGE — compact intelligence moment linking to /faq */}
      <section className="mx-auto max-w-4xl px-5 sm:px-8 py-16" data-testid="home-faq-bridge">
        <div className="relative overflow-hidden rounded-3xl border border-donkey-line glass px-6 sm:px-10 py-8 grid sm:grid-cols-[1.1fr_1fr] gap-6 items-center">
          <div aria-hidden className="absolute -right-10 top-1/2 -translate-y-1/2 w-72 h-72 rounded-full bg-donkey-orange/10 blur-[90px] pointer-events-none" />
          <div className="relative">
            <motion.h2 {...revealProps} className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-donkey-warm">Still got questions?</motion.h2>
            <motion.p {...revealProps} transition={{ ...revealProps.transition, delay: 0.08 }} className="mt-3 text-donkey-grey">Fair questions deserve honest answers.</motion.p>
            <motion.div {...revealProps} transition={{ ...revealProps.transition, delay: 0.16 }}>
              <Button onClick={() => navigate("/faq")} className="mt-6 bg-donkey-orange text-donkey-black hover:bg-donkey-deeporange font-bold rounded-full px-7 h-12 shadow-[0_4px_20px_rgba(249,115,22,0.3)] hover:-translate-y-0.5 transition-all" data-testid="home-faq-cta">
                Ask the Donkey <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </motion.div>
          </div>
          <div className="relative h-[170px] sm:h-[200px]">
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full" aria-hidden>
              {[[12, 22], [8, 62], [34, 14]].map(([x, y], i) => (
                <motion.line key={i} x1={x} y1={y} x2="66" y2="52" stroke="#F5A623" strokeWidth="0.1" vectorEffect="non-scaling-stroke"
                  initial={{ strokeOpacity: 0 }} whileInView={{ strokeOpacity: 0.4 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.2 + i * 0.1 }} />
              ))}
            </svg>
            {[{ l: "COST?", x: "3%", y: "15%" }, { l: "RESULTS?", x: "0%", y: "56%" }, { l: "REFUNDS?", x: "26%", y: "5%" }].map((f, i) => (
              <motion.span key={f.l} className="absolute z-10 inline-flex items-center rounded-full border border-donkey-orange/40 bg-donkey-orange/10 text-donkey-orange px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider backdrop-blur-sm"
                style={{ left: f.x, top: f.y }}
                initial={{ opacity: 0, y: 8, scale: 0.9 }} whileInView={{ opacity: 1, y: 0, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.25 + i * 0.12, ease: "easeOut" }}>
                {f.l}
              </motion.span>
            ))}
            <span aria-hidden className="absolute z-10 w-2.5 h-2.5 rounded-full bg-donkey-orange shadow-[0_0_14px_rgba(249,115,22,0.8)]" style={{ left: "64%", top: "50%", transform: "translate(-50%,-50%)" }} />
            <DonkeyMascot size={150} alt="" className="absolute bottom-0 right-2 sm:right-4 opacity-95 donkey-alive drop-shadow-[0_0_34px_rgba(249,115,22,0.25)]" />
          </div>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section className="relative overflow-hidden border-t border-donkey-line bg-donkey-black">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] rounded-full bg-donkey-orange/10 blur-[120px] pointer-events-none" aria-hidden />
        <div className="relative mx-auto max-w-4xl px-5 sm:px-8 py-24 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-donkey-warm">One plan to prove your idea.</h2>
          <p className="mt-4 text-donkey-warm/80 max-w-2xl mx-auto">Your complete Donkey's Verdict and your first month of validation in one plan — the complete adaptive interview, three opportunities built around you, live market evidence, and a workspace to test real demand.</p>
          <p className="mt-8 font-heading font-extrabold text-5xl text-donkey-orange">£29<span className="text-2xl text-donkey-warm/70">/month</span></p>
          <p className="mt-2 text-sm text-donkey-grey">£29 per month. Cancel anytime securely through Stripe.</p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Button className="bg-donkey-orange text-donkey-black hover:bg-donkey-deeporange font-bold rounded-full px-8 h-12 shadow-[0_4px_20px_rgba(249,115,22,0.3)] hover:-translate-y-0.5 transition-all" onClick={() => navigate("/pricing")} data-testid="home-pricing-btn">
              Start with Prove — £29
            </Button>
            <Button variant="outline" className="border-donkey-line bg-transparent text-donkey-warm hover:border-donkey-orange/60 hover:bg-white/5 rounded-full px-7 h-12" onClick={() => navigate(`/example/${pickSampleSlug()}`)} data-testid="home-sample-verdict-btn">
              See a sample Donkey's Verdict
            </Button>
          </div>
        </div>
      </section>

      {/* FINAL CTA — the closing scene: the Donkey presents the conclusion */}
      <section className="relative overflow-hidden mx-auto max-w-5xl px-5 sm:px-8 py-28 text-center">
        <div className="absolute left-1/2 bottom-[12%] -translate-x-1/2 w-[34rem] max-w-[90vw] h-[16rem] rounded-[50%] bg-donkey-orange/18 blur-[90px] pointer-events-none" aria-hidden />
        <svg className="absolute inset-0 w-full h-full opacity-50 pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
          {[[12, 20], [28, 12], [72, 14], [88, 22], [20, 72], [80, 70]].map(([x, y], i) => (
            <motion.line key={i} x1={x} y1={y} x2="50" y2="52" stroke="#F5A623" strokeWidth="0.1"
              initial={{ strokeOpacity: 0 }} whileInView={{ strokeOpacity: 0.35 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.3 + i * 0.07 }} />
          ))}
        </svg>
        <motion.img
          src="/mascot/verdict.png" alt=""
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto mb-7 h-48 sm:h-56 w-auto object-contain drop-shadow-[0_18px_50px_rgba(0,0,0,0.55)]"
        />
        <motion.p
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
          className="relative text-sm italic text-donkey-amber mb-3"
        >
          “You already know more than you think.”
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.28 }}
          className="relative font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-donkey-warm"
        >
          Let's turn it into something worth testing.
        </motion.h2>
        <motion.div
          initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.4 }}
          className="relative mt-9 flex flex-wrap gap-4 justify-center"
        >
          <Button
            size="lg"
            className="bg-donkey-orange text-donkey-black hover:bg-donkey-deeporange font-bold rounded-full px-8 h-12 text-base transition-all shadow-[0_4px_20px_rgba(249,115,22,0.3)] hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-donkey-orange focus-visible:ring-offset-2 focus-visible:ring-offset-donkey-bg"
            onClick={() => navigate("/pricing")}
            data-testid="final-cta-btn"
          >
            Find my business <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="border-donkey-orange/50 bg-transparent text-donkey-warm hover:bg-donkey-orange/10 hover:border-donkey-orange rounded-full px-7 h-12 text-base focus-visible:ring-2 focus-visible:ring-donkey-orange focus-visible:ring-offset-2 focus-visible:ring-offset-donkey-bg"
            onClick={() => navigate("/demo")}
            data-testid="final-demo-link"
          >
            See the Donkey in action <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </motion.div>
      </section>
    </div>
  );
}
